











































































import Vue from 'vue';
import { mapActions } from 'vuex';
import { format, parseISO } from 'date-fns';

import FileUploadDropzone from '@reciprocity/file-dropzone';
import DocumentAddLink from './DocumentAddLink.vue';
import DocumentsUploads from './DocumentsUploads.vue';

import { Upload, Document, AddLink } from './types';
import StorageService from './storage-service';

interface alertParams {
  type: string;
  message: string;
}

interface confirmParams {
  title: string;
  message: string;
  okTitle: string;
}

declare const GGRC: any;

export default Vue.extend({
  components: {
    FileUploadDropzone,
    DocumentAddLink,
    DocumentsUploads,
  },
  props: {
    documentableType: {
      type: String,
      required: true,
    },
    documentableId: {
      type: String,
      required: true,
    },
    attribute: {
      type: String,
      required: true,
    },
    csrfToken: {
      type: String,
      required: true,
    },
    alwaysInlineDropzoneElements: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      documentsLoading: false,
      documents: [] as Document[],
      uploads: [] as Upload[],
      nextUploadId: 1,
      uploadFile: <boolean>true,

      storageService: new StorageService(this.documentableType, this.documentableId, this.csrfToken),
    };
  },
  computed: {
    runsAsSpa() {
      return !!this.$store;
    },
    inlinedDropzoneElements() {
      return this.alwaysInlineDropzoneElements || this.documents.length || this.uploads.length;
    },
  },
  watch: {
    documents: function(documents) {
      this.$emit('attachmentsCount', documents.length);
    },
  },
  created() {
    this.loadDocuments();
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
      showModal: 'modals/show',
    }),
    alert(params: alertParams) {
      if (this.runsAsSpa) {
        this.showAlert(params);
      } else {
        window.GGRC.Notify(params);
      }
    },
    async confirm(params: confirmParams): Promise<boolean> {
      if (this.runsAsSpa) {
        try {
          return await this.showModal({
            type: 'GenericConfirm',
            data: params,
          });
        } catch (err) {
          return false;
        }
      } else {
        return await window.GGRC.zenModal.confirm(params.message);
      }
    },
    tableRowClass(idx: number) {
      return idx % 2 === 0 ? 'even' : 'odd';
    },
    loadDocuments() {
      this.documentsLoading = true;

      return this.storageService.getDocuments().then(
        res => {
          this.documents = res;
          this.documentsLoading = false;
        },
        (err: any) => {
          this.alert({
            type: 'error',
            message: `Failed to load files: ${err}`,
          });
        }
      );
    },
    createUpload(title: string) {
      const uploadId = this.nextUploadId++;
      const upload: Upload = {
        id: uploadId,
        title: title,
        progress: 0,
      };

      this.uploads.push(upload);

      return upload;
    },
    removeUpload(upload: Upload) {
      const idx = this.uploads.indexOf(upload);

      if (idx !== -1) {
        this.uploads.splice(idx, 1);
      }
    },
    handleFile(file: File) {
      const upload = this.createUpload(file.name);

      const onProgress = (progress: number) => {
        upload.progress = Math.round(progress * 100);
      };

      const cancelPromise = new Promise<void>(resolve => {
        upload.cancel = resolve;
      });
      return this.storageService.uploadFile(file, onProgress, cancelPromise).then(
        () => {
          return this.loadDocuments().then(() => {
            this.removeUpload(upload);
          });
        },
        (err: any) => {
          this.removeUpload(upload);
          if (err !== 'aborted') {
            var msg = (err && err.responseJSON && err.responseJSON.message) || 'Internal server error.';

            if (err.status == 413) {
              msg = 'File size limit exceeded';
            }

            this.alert({
              type: 'error',
              message: `Failed to upload file ${file.name}: ${msg}`,
            });
          }
        }
      );
    },
    handleAddLink({ link, title }: AddLink) {
      const upload = this.createUpload(title);
      upload.progress = 50;

      return this.storageService.addLink(link, title).then(
        () => {
          return this.loadDocuments().then(() => {
            this.removeUpload(upload);
          });
        },
        (err: any) => {
          this.removeUpload(upload);

          const msg = (err && err.responseJSON && err.responseJSON.message) || 'Internal server error.';

          this.alert({
            type: 'error',
            message: `Failed to add link ${link}: ${msg}`,
          });
        }
      );
    },
    async deleteDocument(document: Document) {
      const confirmMsg = `Do you really want to remove ${document.title}?`;

      if (
        await this.confirm({
          title: 'Remove attachment?',
          message: confirmMsg,
          okTitle: 'Remove',
        })
      ) {
        return this.storageService.deleteDocument(document.id).then(
          () => {
            const idx = this.documents.indexOf(document);
            if (idx !== -1) {
              this.documents.splice(idx, 1);
            }
            return this.loadDocuments();
          },
          (err: any) => {
            const msg = (err && err.responseJSON && err.responseJSON.message) || 'Internal server error.';

            this.alert({
              type: 'error',
              message: `Failed to remove file ${document.title}: ${msg}.`,
            });
          }
        );
      }
      return null;
    },
    renameDocument(document: Document) {
      const promptMsg = `Enter new name for '${document.title}':`;

      const newTitle = prompt(promptMsg, document.title);

      if (newTitle && newTitle !== document.title) {
        return this.storageService.renameDocument(document.id, newTitle).then(
          () => {
            document.title = newTitle;

            return this.loadDocuments();
          },
          (err: any) => {
            const msg = (err && err.responseJSON && err.responseJSON.message) || 'Internal server error.';

            this.alert({
              type: 'error',
              message: `Failed to rename file ${document.title}: ${msg}.`,
            });
          }
        );
      }

      return null;
    },
    createdDisplay(created: string) {
      return format(parseISO(created), `M/d/yyyy 'at' h:mm a 'UTC'`);
    },
    switchType(ev: any) {
      ev.preventDefault();
      this.uploadFile = !this.uploadFile;
    },
  },
});
